import React from "react";
import SEO from "../../../components/SEO";
import img34 from "../../../assets/img34.webp";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import img35 from "../../../assets/img35.webp";
import img36 from "../../../assets/img36.webp";
import img37 from "../../../assets/img37.webp";
import img38 from "../../../assets/img38.webp";
import BlogSection from "../../../components/BlogSection";

export default function InternationalStudentInsuranceHero() {
  const container = "w-full max-w-[1380px] mx-auto px-4 sm:px-6 lg:px-8";

  return (
    <>
      <SEO
        title="Assurance étudiants internationaux au Canada – einsured.ca"
        description="Comparez les assurances santé pour étudiants internationaux au Canada et choisissez une couverture adaptée à vos besoins avec einsured.ca."
        keywords="assurance étudiants internationaux Canada, régime de santé étudiant canada, UHIP ontario, assurance maladie privée pour étudiants"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start text-center md:text-left"
        style={{ backgroundImage: `url(${img34})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className={`relative z-10 ${container}`}>
          <div className="text-white max-w-lg">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Assurance pour étudiants internationaux
            </h1>

            <p className="text-base sm:text-lg md:text-xl font-semibold mb-8">
              Sécurisez votre parcours d&apos;études à l&apos;étranger avec une assurance médicale
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* INFO SECTION */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-8">
              Assurance pour étudiants internationaux
            </h2>

            <div className="space-y-6 text-base sm:text-lg leading-7 sm:leading-8">
              <p>
                Poursuivre ses études à l&apos;étranger est une aventure passionnante, mais elle comporte
                ses propres défis, notamment comprendre et obtenir la bonne couverture d&apos;assurance.
              </p>

              <p>
                Pour les{" "}
                <span className="font-semibold text-[#028fdc]">
                  étudiants internationaux au Canada
                </span>
                , naviguer dans les complexités des systèmes de santé et d&apos;assurance dans un nouveau pays
                peut être intimidant. Le Canada est reconnu pour la qualité de son éducation et son
                attitude accueillante envers les étudiants du monde entier, mais se préparer à votre séjour
                va bien au-delà de faire vos bagages. L&apos;assurance est un élément essentiel de votre
                planification, vous assurant une couverture en cas de problèmes de santé imprévus ou d&apos;urgences.
              </p>

              <p>
                Ce guide vise à démystifier l&apos;assurance pour étudiants internationaux au Canada,
                couvrant tout, des types d&apos;assurance disponibles aux conseils pour choisir le bon régime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* UNDERSTANDING HEALTHCARE IN CANADA*/}
      <section
        className="relative py-16 sm:py-20 md:py-24 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900/80" />

        <div className={`relative z-10 ${container}`}>
          <div className="text-center text-white mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
              Comprendre le système de santé au Canada
            </h2>
            <p className="text-base sm:text-lg leading-relaxed max-w-4xl mx-auto">
              <span className="font-semibold">Le système de santé canadien</span>{" "}
              est reconnu dans le monde entier pour la qualité de ses soins. C&apos;est un système financé
              par l&apos;État, ce qui signifie que les citoyens canadiens et les résidents permanents peuvent
              accéder à la plupart des services de santé à peu ou pas de frais directs. Cependant, ce système
              ne couvre pas automatiquement les étudiants internationaux, d&apos;où l&apos;importance de l&apos;assurance
              maladie privée.
            </p>
          </div>

          <div className="bg-white rounded-[32px] shadow-xl px-6 sm:px-8 md:px-10 py-6 sm:py-8 md:py-10 mb-8 sm:mb-10">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
              Régimes d&apos;assurance maladie provinciaux et territoriaux
            </h3>

            <div className="space-y-4 text-base sm:text-lg leading-relaxed text-gray-800">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="text-[#49628b] font-bold">
                    Colombie-Britannique, Alberta et Saskatchewan :
                  </span>{" "}
                  Les étudiants internationaux sont admissibles à la couverture provinciale de santé,
                  mais doivent en faire la demande. Il y a généralement une période d&apos;attente avant
                  que la couverture entre en vigueur.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="text-[#49628b] font-bold">Ontario :</span>{" "}
                  Les étudiants internationaux doivent s&apos;inscrire au Régime d&apos;assurance maladie
                  universitaire (UHIP), qui offre une couverture similaire au Régime d&apos;assurance-santé
                  de l&apos;Ontario (OHIP) pour les résidents. Il peut être offert par les établissements
                  d&apos;enseignement respectifs par l&apos;intermédiaire d&apos;alliances d&apos;assurance ou peut être
                  acheté auprès de conseillers en assurance indépendants.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="text-[#49628b] font-bold">Québec :</span>{" "}
                  Les étudiants des pays ayant une entente de sécurité sociale avec le Québec peuvent
                  être admissibles au Régime d&apos;assurance maladie du Québec. Les autres doivent souscrire
                  une assurance privée.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="text-[#49628b] font-bold">
                    Terre-Neuve-et-Labrador, Nouvelle-Écosse, Nouveau-Brunswick,
                    Île-du-Prince-Édouard, Manitoba et les territoires :
                  </span>{" "}
                  Les politiques varient considérablement, et il est important de vérifier les exigences
                  spécifiques de chaque juridiction.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 mb-8 sm:mb-10">
            <div className="bg-white rounded-[32px] shadow-xl px-6 sm:px-8 md:px-10 py-6 sm:py-8 md:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Régimes de santé universitaires et collégiaux
              </h3>
              <div className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-gray-800">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  De nombreux établissements d&apos;enseignement canadiens offrent des régimes d&apos;assurance
                  maladie à leurs étudiants internationaux. Ces régimes sont souvent obligatoires, avec
                  le coût inclus dans vos frais de scolarité. Ils sont adaptés pour répondre aux besoins
                  des étudiants, couvrant des services de soins de santé de base à étendus, y compris les
                  visites chez le médecin, l&apos;hospitalisation, et parfois même les soins dentaires et de la vue.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-[32px] shadow-xl px-6 sm:px-8 md:px-10 py-6 sm:py-8 md:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Assurance maladie privée pour étudiants internationaux
              </h3>
              <div className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-gray-800">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  De nombreux établissements d&apos;enseignement ont des partenariats avec des fournisseurs
                  d&apos;assurance privée pour offrir une couverture santé à leurs étudiants internationaux.
                  Ces régimes sont généralement obligatoires et sont conçus pour s&apos;assurer que les
                  étudiants satisfont aux exigences de couverture de soins de santé pendant leurs études.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-10 py-4 rounded-full shadow-md transition"
            >
              Planifier une consultation gratuite
            </a>
          </div>
        </div>
      </section>

      {/* ACCESSING HEALTHCARE SERVICES SECTION */}
      <section className="py-16 sm:py-20 md:py-22 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-4">
              Accès aux services de santé
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-4xl mx-auto mb-10 sm:mb-12">
              Avec une assurance maladie en place, les étudiants internationaux peuvent accéder
              à une gamme de services de santé. Voici ce que vous devez savoir
              pour tirer le meilleur parti de votre couverture.
            </p>

            <div className="grid md:grid-cols-2 gap-8 sm:gap-10 items-center mb-10 sm:mb-12">
              <div className="rounded-[32px] overflow-hidden shadow-md">
                <img
                  src={img35}
                  alt="Doctor with stethoscope"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Trouver un médecin ou un prestataire de soins de santé
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-7 sm:leading-8">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    Les médecins de soins primaires, ou médecins de famille, sont souvent le premier
                    point de contact pour les problèmes de santé non urgents. Vous pouvez trouver un
                    médecin grâce aux recommandations d&apos;amis, de votre école ou des répertoires en ligne.
                    Les cliniques acceptant de nouveaux patients peuvent également être répertoriées sur
                    les sites Web provinciaux de santé.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 sm:gap-10 items-center mb-10 sm:mb-12">
              <div className="order-2 md:order-1">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Visiter un hôpital ou une clinique
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-7 sm:leading-8">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    En cas d&apos;urgences non mortelles ou de problèmes de santé spécifiques, vous pouvez
                    vous rendre dans des cliniques sans rendez-vous ou des centres de soins urgents.
                    Pour les urgences graves, rendez-vous directement à la salle d&apos;urgence de l&apos;hôpital
                    le plus proche. N&apos;oubliez pas que votre{" "}
                    <span className="text-[#20a7df] font-semibold">
                      carte d&apos;assurance maladie
                    </span>{" "}
                    ou les détails de votre régime doivent toujours être facilement accessibles.
                  </p>
                </div>
              </div>
              <div className="order-1 md:order-2 rounded-[32px] overflow-hidden shadow-md">
                <img
                  loading="lazy"
                  src={img36}
                  alt="Hospital building"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 sm:gap-10 items-center mb-10 sm:mb-12">
              <div className="rounded-[32px] overflow-hidden shadow-md">
                <img
                  loading="lazy"
                  src={img37}
                  alt="Prescription medications and pill organizer"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Médicaments sur ordonnance et pharmacies
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-7 sm:leading-8">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    Les ordonnances sont généralement requises pour les médicaments au Canada.
                    Les pharmacies sont répandues, y compris dans certains grands magasins de détail.
                    La couverture des médicaments varie selon le régime d&apos;assurance, vérifiez donc
                    ce que votre régime couvre.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div className="order-2 md:order-1">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                  Accéder aux services de santé mentale
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-7 sm:leading-8">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    La santé mentale est une composante essentielle du bien-être général, et de nombreux
                    établissements offrent des services spécifiques aux étudiants, notamment le counseling
                    et la thérapie, souvent couverts par les régimes de santé étudiants. N&apos;hésitez pas
                    à recourir à ces services si vous avez besoin de soutien.
                  </p>
                </div>
              </div>
              <div className="order-1 md:order-2 rounded-[32px] overflow-hidden shadow-md">
                <img
                  loading="lazy"
                  src={img38}
                  alt="Mental health support conversation"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERNATIONAL STUDENT INSURANCE TIPS & BEST PRACTICES */}
      <section className="py-16 sm:py-20 md:py-12 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8 sm:mb-10">
              Conseils et meilleures pratiques en matière d&apos;assurance pour étudiants internationaux
            </h2>

            <div className="space-y-6 text-base sm:text-lg leading-7 sm:leading-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Comprendre votre couverture :
                  </span>{" "}
                  Examinez attentivement votre police d&apos;assurance médicale d&apos;urgence pour
                  savoir ce qui est couvert et ce qui ne l&apos;est pas.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Gardez les documents importants accessibles :
                  </span>{" "}
                  Apportez toujours votre numéro de police d&apos;assurance médicale ou votre documentation,
                  surtout lorsque vous accédez aux services de santé.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Connaître la procédure de réclamation :
                  </span>{" "}
                  Familiarisez-vous avec le processus de soumission des réclamations d&apos;assurance,
                  y compris les documents nécessaires ou les délais.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Être conscient des exigences en matière de santé et de vaccination :
                  </span>{" "}
                  Certaines provinces ou institutions peuvent avoir des exigences spécifiques en matière
                  de santé et de vaccination pour les étudiants internationaux.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  <span className="font-bold text-[#49628b]">
                    Envisager une couverture supplémentaire :
                  </span>{" "}
                  Selon vos activités et votre mode de vie, vous pourriez avoir besoin d&apos;une couverture
                  supplémentaire, comme les soins dentaires ou de la vue, non inclus dans les régimes
                  d&apos;assurance médicale d&apos;urgence de base.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHOOSING THE RIGHT INSURANCE PLAN */}
      <section className="py-12 sm:py-16 md:py-14 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#324f7b] text-center mb-8 sm:mb-10">
              Choisir le bon régime d&apos;assurance
            </h2>

            <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-8 md:mb-10">
              <div className="bg-[#f5f7fb] rounded-[32px] shadow-md px-6 sm:px-8 py-6 sm:py-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#324f7b] mb-4">
                  Couverture
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-gray-700">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    Examinez attentivement ce que chaque régime couvre. Recherchez les détails sur
                    les visites à l&apos;hôpital, les médicaments sur ordonnance, les soins dentaires et
                    si les conditions préexistantes sont couvertes. Comprendre l&apos;étendue de la couverture
                    vous aidera à éviter des coûts inattendus par la suite.
                  </p>
                </div>
              </div>

              <div className="bg-[#f5f7fb] rounded-[32px] shadow-md px-6 sm:px-8 py-6 sm:py-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#324f7b] mb-4">
                  Coût
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-gray-700">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    Comparez le coût des différents régimes, y compris les primes, les franchises et
                    les quotes-parts. Les régimes moins chers peuvent sembler attrayants, mais
                    considérez s&apos;ils offrent une couverture suffisante pour vos besoins.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-10">
              <div className="bg-[#f5f7fb] rounded-[32px] shadow-md px-6 sm:px-8 py-6 sm:py-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#324f7b] mb-4">
                  Validité
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-gray-700">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    Vérifiez la validité du régime d&apos;assurance. Certains régimes ne vous couvrent
                    que pendant l&apos;année académique ou nécessitent un renouvellement annuel. Assurez-vous
                    que votre couverture ne s&apos;interrompt pas si vous prévoyez rester au Canada pendant
                    les vacances ou arriver avant le début de votre programme d&apos;études.
                  </p>
                </div>
              </div>

              <div className="bg-[#f5f7fb] rounded-[32px] shadow-md px-6 sm:px-8 py-6 sm:py-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#324f7b] mb-4">
                  Processus de réclamation
                </h3>
                <div className="flex items-start gap-3 text-base sm:text-lg leading-relaxed text-gray-700">
                  <CheckCircle className="h-5 w-5 text-[#00b4ff] mt-1 shrink-0" />
                  <p>
                    Comprenez comment déposer une réclamation. Recherchez des assureurs qui offrent
                    un processus de réclamation simple et une assistance en plusieurs langues, si
                    nécessaire. Un traitement rapide et efficace des réclamations peut faire une
                    grande différence lorsque vous avez besoin de soins médicaux.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Obtenir le meilleur service
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONCLUSION  */}
      <section className="bg-[#dbe4f0] border-y-4 border-[#2b4672] py-12 sm:py-16 md:py-16">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-center text-[#324f7b]">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8">
              Conclusion
            </h2>

            <div className="space-y-4 text-base sm:text-lg leading-7 sm:leading-8">
              <p>
                Obtenir la bonne assurance maladie est une étape cruciale dans votre préparation
                pour étudier au Canada. Cela répond non seulement à une exigence légale, mais vous
                procure également la tranquillité d&apos;esprit, vous permettant de vous concentrer sur vos
                études et de profiter de votre aventure internationale sans vous soucier des frais
                médicaux imprévus. Prenez le temps de faire des recherches et de choisir un régime
                qui offre la couverture dont vous avez besoin à un prix que vous pouvez vous permettre.
                N&apos;oubliez pas que le meilleur régime d&apos;assurance est celui qui correspond à vos besoins
                uniques en tant qu&apos;étudiant international au Canada.
              </p>

              <p>
                Alors que vous entamez ce chapitre passionnant de votre vie, gardez ce guide à
                l&apos;esprit pour naviguer avec confiance dans le monde de l&apos;assurance pour étudiants
                internationaux au Canada.
              </p>

              <p>
                Bienvenue au Canada ! et voici à une expérience éducative saine, sûre et enrichissante.
              </p>
            </div>

            <div className="mt-10 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition"
              >
                Réserver une réunion maintenant
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers blogues" mode="related" relatedCount={3} />
    </>
  );
}
